import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ClipboardList, Video, FileCheck, TrendingUp, ArrowRight } from 'lucide-react';

const steps = [
  {
    step: '01',
    icon: ClipboardList,
    title: 'Pre-Session Intelligence',
    description: 'AI reviews patient history, past notes, assessments, and mood tracking data to generate personalized session goals and preparation insights before you even start.',
    color: 'from-brand-500 to-brand-600',
  },
  {
    step: '02',
    icon: Video,
    title: 'AI-Guided Session',
    description: 'During the session, real-time transcription captures the conversation while AI provides clinical prompts, detects emotional patterns, and tracks key topics.',
    color: 'from-accent-500 to-emerald-600',
  },
  {
    step: '03',
    icon: FileCheck,
    title: 'Instant Documentation',
    description: 'The moment the session ends, your SOAP notes, progress summaries, ICD codes, and claim forms are ready. Review, edit if needed, and submit.',
    color: 'from-violet-500 to-purple-600',
  },
  {
    step: '04',
    icon: TrendingUp,
    title: 'Continuous Care Intelligence',
    description: 'AI updates treatment plans based on session insights, tracks patient outcomes over time, and surfaces trends that inform better clinical decisions.',
    color: 'from-amber-500 to-orange-600',
  },
];

export default function HowItWorks() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section id="how-it-works" className="section-padding bg-brand-950 relative overflow-hidden" ref={ref}>
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-60" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />

      <div className="container-max relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
            AI That Works{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-400 to-accent-300">
              Before, During & After
            </span>{' '}
            Every Session
          </h2>
          <p className="text-lg text-brand-200/80">
            A complete AI-augmented clinical workflow. No other platform offers this.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="relative group"
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-12 left-full w-full h-px bg-gradient-to-r from-white/20 to-transparent z-10" />
              )}

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <step.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-3xl font-extrabold text-white/10">{step.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-brand-200/70 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <Link
            to="/register"
            className="btn-accent !py-4 !px-8 !text-base group"
          >
            Experience the Difference
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
